import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { redeemIntakeLink } from '../services/api';
import { clearAuthState } from '../services/authStorage';

/**
 * Pre-authenticated Orbit entry. A consultant shares /orbit/enter/<token>;
 * this page exchanges the token for a real client session and drops the
 * recipient into their Orbit deck as that client, no sign-in. The token in the
 * URL is the auth boundary (validated server-side).
 */
const OrbitEnter: React.FC = () => {
    const navigate = useNavigate();
    const { token } = useParams<{ token: string }>();
    const ran = useRef(false);

    useEffect(() => {
        if (ran.current) return; // guard React 18 StrictMode double-invoke
        ran.current = true;

        if (!token) {
            toast.error('Invalid link.');
            navigate('/login');
            return;
        }

        redeemIntakeLink(token)
            .then(({ token: jwt, refresh_token: refreshToken, user }) => {
                // Mirror OAuthCallback: clearAuthState() drops the prior
                // session but preserves orbit:* domain data so a returning
                // client keeps their chat history.
                clearAuthState();
                localStorage.setItem('auth_token', jwt);
                // The access token lasts minutes; the refresh token keeps the
                // owner signed in, as it does after a password sign-in.
                if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
                localStorage.setItem('user_role', user.role || 'client');
                localStorage.setItem('user_id', user.id || '');
                localStorage.setItem('user_email', user.email || '');
                localStorage.setItem('user_name', user.name || '');
                if (user.client_id) localStorage.setItem('client_id', user.client_id);
                // The partner created this account with a random password. The
                // setup asks the owner to set their own first, so the next visit
                // works without the link.
                localStorage.setItem('orbit_set_password', '1');
                // The link opens on the owner's setup, which asks for the
                // password first.
                window.location.href = '/setup';
            })
            .catch((err) => {
                const detail = axios.isAxiosError(err) ? err.response?.data?.detail : undefined;
                // The link signs the owner in once. Opened again, it sends them
                // to sign in, where the login page says why.
                if (detail === 'intake_link_used') {
                    navigate('/login?error=intake_link_used');
                    return;
                }
                toast.error(typeof detail === 'string' && detail ? detail : 'This link is invalid or expired.');
                navigate('/login');
            });
    }, [token, navigate]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-white">
            <div className="text-center">
                <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-sm text-gray-500">Opening your Orbit deck</p>
            </div>
        </div>
    );
};

export default OrbitEnter;
